/**
 * @fileoverview OptimizationJobManager service
 * Handles job lifecycle management, storage, and state tracking
 * Extracted from EnhancedBacktestService for separation of concerns
 */

import { apiClient } from '../api/client';
import type { OptimizationJob } from './OptimizationEventService';

export class OptimizationJobManager {
  private readonly maxJobStoreAttempts = 5;
  private storedJobIds = new Set<string>();
  private jobStoreAttempts = new Map<string, number>();
  private pendingJobStores = new Set<string>();

  /**
   * Get current active optimization jobs
   */
  async getJobs(): Promise<OptimizationJob[]> {
    try {
      const response = await apiClient.get<{
        success: boolean;
        data: OptimizationJob[];
        error?: string;
      }>('/optimize/jobs');

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch optimization jobs');
      }

      return response.data || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Cancel a running optimization job
   */
  async cancelJob(optimizationId: string): Promise<void> {
    try {
      const response = await apiClient.post<{
        success: boolean;
        error?: string;
      }>(`/optimize/cancel/${optimizationId}`, {});

      if (!response.success) {
        throw new Error(response.error || 'Failed to cancel optimization');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Remove a completed optimization job
   */
  async removeJob(optimizationId: string): Promise<void> {
    try {
      const response = await apiClient.delete<{
        success: boolean;
        error?: string;
      }>(`/optimize/jobs/${optimizationId}`);

      if (!response.success) {
        throw new Error(response.error || 'Failed to remove optimization job');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Store completed optimization job in database for persistence
   * Includes retry logic and duplicate prevention
   */
  async storeCompletedJob(job: OptimizationJob): Promise<void> {
    // Skip if already stored
    if (this.storedJobIds.has(job.id)) {
      return;
    }

    // Check retry attempts
    const currentAttempts = this.jobStoreAttempts.get(job.id) ?? 0;
    if (currentAttempts >= this.maxJobStoreAttempts) {
      return;
    }

    // Skip if already pending
    if (this.pendingJobStores.has(job.id)) {
      return;
    }

    this.pendingJobStores.add(job.id);

    try {
      // Extract parameterRanges and optimizationTarget from job
      // Note: These may be at the top level OR nested in job.parameters depending on source
      const parameterRanges = job.parameterRanges || job.parameters?.parameterRanges;
      const optimizationTarget = job.optimizationTarget || job.parameters?.optimizationTarget;
      
      await apiClient.post('/optimize/jobs/store', {
        id: job.id,  // Backend expects 'id', not 'jobId'
        status: job.status,
        strategy: job.strategy,
        symbol: job.symbol,
        interval: job.interval,
        startTime: job.startTime,
        completedAt: new Date().toISOString(),
        progress: job.progress,
        bestResult: job.bestResult,
        error: job.error,
        estimatedDuration: job.estimatedDuration,
        // Include optimization-specific data for proper history loading
        allResults: job.allResults,
        parameterRanges: parameterRanges,
        optimizationTarget: optimizationTarget
      });
      
      this.storedJobIds.add(job.id);
      this.jobStoreAttempts.delete(job.id);
    } catch (error) {
      this.jobStoreAttempts.set(job.id, currentAttempts + 1);
      // Don't throw - this is not critical for operation
    } finally {
      this.pendingJobStores.delete(job.id);
    }
  }
}

// Export singleton instance
export const optimizationJobManager = new OptimizationJobManager();
